import { useMemo, useState } from 'react';

import { styled } from '../../../../../../stitches.config';
import { Popover } from '../../../../../containers/Popover';
import { Icon } from '../../../../../text/Icon/Icon';
import { InfoBox } from '../../../../../text/InfoBox/InfoBox';
import { Button } from '../../../../Button/Button';
import { RadioGroup, RadioGroupInput } from '../../../../Radio/RadioGroup';
import { CompoundPreview } from '../../InsertCompound/components/CompoundPreview';
import { CommonMol } from '../services/getCommonMols';

const Filters = styled('div', {
  marginBottom: '$5',
  fontSize: '$2',
});

const FilterGroup = styled('div', {
  display: 'flex',
  gap: '$6',
  marginBottom: '$4',
});

const Filter = styled('div', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '$4',
  color: '$slate11',
  fontWeight: 300,
});

const FilterTextInput = styled('input', {
  'all': 'unset',
  'backgroundColor': '$slate3',
  'padding': '$2 $4',
  'borderRadius': '0.5rem',
  '&:focus': {
    backgroundColor: '$slate4',
  },
});

const FragmentContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gridGap: '$3',
  maxHeight: '20rem',
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '$3 $6 $7 0',
  marginRight: '-$6',
});

const CompoundPreviewContainer = styled('div', {
  margin: '$3',
});

const FragmentSelectionInfo = styled('p', {
  display: 'flex',
  alignItems: 'center',
  gap: '$3',
  fontSize: '$2',
  fontWeight: 300,
  color: '$slate10',
  marginTop: '$4',
});

const elementFiltersState = [
  {
    name: 'Oxygens',
    key: 'oxygens',
    atom: 'O',
    selection: 'any',
  },
  {
    name: 'Nitrogens',
    key: 'nitrogens',
    atom: 'N',
    selection: 'any',
  },
  {
    name: 'Sulphurs',
    key: 'sulphurs',
    atom: 'S',
    selection: 'any',
  },
];

type FragmentPickerProps = {
  mols: CommonMol[];
  onPickAtom: (mol: CommonMol, defaultIdx: string) => void;
  onPickBond: (mol: CommonMol, defaultIdx: string) => void;
};
export const FragmentPicker = ({ mols, onPickAtom, onPickBond }: FragmentPickerProps) => {
  const [open, setOpen] = useState(false);
  const [elementFilters, setElementFilters] = useState(elementFiltersState);
  const [ringCount, setRingCount] = useState<'any' | number>('any');
  const [name, setName] = useState('');

  const visibleMols = useMemo(() => {
    return mols.filter(mol => {
      const matchesRings = ringCount === 'any' || ringCount === mol.ringsCount;
      const matchesName = !name || String(mol.key).toLowerCase().includes(String(name).toLowerCase());
      const matchesElements = elementFilters.every(element => {
        if (element.selection === 'any') {
          return true;
        }
        const molHasElement = mol.elements.has(element.atom);
        if (element.selection === 'some') {
          return molHasElement;
        }
        return !molHasElement;
      });
      return matchesRings && matchesName && matchesElements;
    });
  }, [mols, ringCount, name, elementFilters]);

  return (
    <Popover
      open={open}
      onOpenChange={open => {
        setOpen(open);
      }}
      trigger={<Button weight="minimal" icon="more_vert" active={open} />}
      contentProps={{
        side: 'left',
        css: { backgroundColor: '$slate2' },
      }}
    >
      <Filters>
        <FilterGroup>
          {elementFilters.map(element => (
            <Filter key={element.key}>
              {element.name}:
              <RadioGroup
                value={String(element.selection)}
                onValueChange={value => {
                  const updatedFilters = elementFilters.map(e =>
                    e.key === element.key
                      ? {
                          ...element,
                          selection: value,
                        }
                      : e
                  );
                  setElementFilters(updatedFilters);
                }}
                aria-label="Ring count"
              >
                <RadioGroupInput value="any" id={`${element.key}-0`} label="Any" />
                <RadioGroupInput value="some" id={`${element.key}-1`} label="Some" />
                <RadioGroupInput value="none" id={`${element.key}-2`} label="None" />
              </RadioGroup>
            </Filter>
          ))}
        </FilterGroup>
        <FilterGroup>
          <Filter>
            Rings
            <RadioGroup
              value={String(ringCount)}
              onValueChange={value => {
                setRingCount(value === 'any' ? 'any' : Number(value));
              }}
              aria-label="Ring count"
            >
              <RadioGroupInput value="any" id="rn" label="Any" />
              <RadioGroupInput value="0" id="r0" label="0" />
              <RadioGroupInput value="1" id="r1" label="1" />
              <RadioGroupInput value="2" id="r2" label="2" />
              <RadioGroupInput value="3" id="r3" label="3" />
            </RadioGroup>
          </Filter>
          <Filter>
            Name
            <FilterTextInput
              type="text"
              value={name}
              onChange={event => {
                setName(event.target.value);
              }}
            />
          </Filter>
        </FilterGroup>
      </Filters>

      {visibleMols.length === 0 && (
        <InfoBox title="No matching compounds" icon="stream">
          Please change the active filters.
        </InfoBox>
      )}
      <FragmentContainer>
        {visibleMols.map((mol, idx) => (
          <CompoundPreviewContainer key={mol.key}>
            <CompoundPreview
              mol={mol}
              onMouseDown={(_: any, molEvent: any) => {
                if (molEvent.atom) {
                  onPickAtom(mol, molEvent.idx);
                  setOpen(false);
                } else if (molEvent.bond) {
                  onPickBond(mol, molEvent.idx);
                  setOpen(false);
                }
              }}
              renderDelay={idx * 30}
              text={String(mol.key)}
            />
          </CompoundPreviewContainer>
        ))}
      </FragmentContainer>
      {visibleMols.length !== 0 && (
        <FragmentSelectionInfo>
          <Icon icon="info" /> Select atom or bond to insert by
        </FragmentSelectionInfo>
      )}
    </Popover>
  );
};
